// src/components/Common/NotificationSystem.jsx - Complete version
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

// Individual Notification Component
const Notification = ({ notification, onRemove }) => {
  const [isExiting, setIsExiting] = useState(false);

  const handleRemove = () => {
    setIsExiting(true);
    setTimeout(() => {
      onRemove(notification.id);
    }, 300); // Match CSS animation duration
  };

  useEffect(() => {
    if (notification.duration > 0) {
      const timer = setTimeout(() => {
        handleRemove();
      }, notification.duration);

      return () => clearTimeout(timer);
    }
  }, [notification.duration, handleRemove]);

  const getIcon = () => {
    switch (notification.type) {
      case 'success':
        return '✓';
      case 'error':
        return '✕';
      case 'warning':
        return '⚠';
      case 'info':
        return 'ℹ';
      default:
        return '✓';
    }
  };

  return (
    <div 
      className={`notification notification-${notification.type} ${isExiting ? 'removing' : ''}`}
      onClick={handleRemove}
      style={{
        '--duration': `${notification.duration}ms`
      }}
    >
      <div className="notification-content">
        <span className="notification-icon">
          {getIcon()}
        </span>
        <span className="notification-message">
          {notification.message}
        </span>
      </div>
      <button 
        className="notification-close"
        onClick={(e) => {
          e.stopPropagation();
          handleRemove();
        }}
        aria-label="Close notification"
      >
        ×
      </button>
    </div>
  );
};

// Main Notification System Component
const NotificationSystem = ({ notifications, onRemove, floating = false }) => {
  if (!notifications || notifications.length === 0) return null;

  const notificationContent = (
    <div className={`notification-container ${floating ? 'floating' : ''}`}>
      {notifications.map(notification => (
        <Notification
          key={notification.id}
          notification={notification}
          onRemove={onRemove}
        />
      ))}
    </div>
  );

  return createPortal(notificationContent, document.body);
};

export default NotificationSystem;

// Hook for using notifications
export const useNotifications = () => {
  const [notifications, setNotifications] = useState([]);

  const addNotification = (message, type = 'success', duration = 4000) => {
    const id = Date.now() + Math.random();
    const notification = { 
      id, 
      message, 
      type, 
      duration,
      createdAt: new Date()
    };
    
    setNotifications(prev => [...prev, notification]);
    
    return id;
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const clearAllNotifications = () => {
    setNotifications([]);
  };

  return {
    notifications,
    addNotification,
    removeNotification,
    clearAllNotifications
  };
};